<template>
  <div v-if="show" class="bryson-import-overlay" @click.self="handleClose">
    <div ref="brysonImportModal" class="bryson-import-modal draggable-popup">
      <div class="bryson-import-header draggable-header">
        <h3>Bryson Lawncare Import</h3>
        <button class="close-btn" @click="handleClose">X</button>
      </div>

      <div v-if="isBusy" class="busy-overlay">
        <div class="spinner"></div>
        <div class="busy-label">{{ busyMessage }}</div>
      </div>

      <div class="bryson-import-body">
        <div class="section-header-row">
          <div class="section-header">1) Select Unread Bryson Emails</div>
          <button
            v-if="processedRows.length"
            class="secondary toggle-email-table-btn"
            @click="showEmailTable = !showEmailTable"
          >
            {{ showEmailTable ? 'Hide Email Table' : 'Show Email Table' }}
          </button>
        </div>

        <div v-if="showEmailTable || !processedRows.length">
          <div class="email-table-wrap">
            <table class="email-table">
              <thead>
                <tr>
                  <th class="check-col"></th>
                  <th class="date-col">Date</th>
                  <th>Subject</th>
                  <th class="invoice-col">Invoice</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="email in emails"
                  :key="email.uid"
                  :class="{ 'selected-row': isEmailSelected(email.uid) }"
                  @click="toggleEmail(email.uid)"
                >
                  <td class="check-col">
                    <input
                      type="checkbox"
                      :checked="isEmailSelected(email.uid)"
                      @change.stop="toggleEmail(email.uid)"
                    />
                  </td>
                  <td class="date-col">{{ formatDate(email.emailDate) }}</td>
                  <td>{{ email.subject }}</td>
                  <td class="invoice-col">{{ email.invoiceNumber }}</td>
                </tr>
                <tr v-if="!emails.length && !isLoadingEmails">
                  <td colspan="4" class="hint">
                    No unread Bryson payment request emails were found.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="email-actions">
            <button
              class="secondary"
              :disabled="!selectedEmailUids.length || isProcessing"
              @click="processSelection"
            >
              {{ isProcessing ? 'Processing...' : 'Process Selected' }}
            </button>
            <button
              class="secondary"
              :disabled="!emails.length || isProcessing || isSaving"
              @click="selectAllEmails"
            >
              Select All
            </button>
            <button
              class="secondary"
              :disabled="isLoadingEmails"
              @click="loadEmails"
            >
              Refresh
            </button>
            <button
              class="secondary"
              :class="{ 'verbose-active': showVerbosePanel }"
              :disabled="isLoadingEmails"
              @click="toggleVerbosePanel"
            >
              Verbose
            </button>
          </div>
          <div v-if="showVerbosePanel" class="imap-verbose-panel">
            <div class="imap-verbose-title">
              <span>IMAP connection log</span>
              <span v-if="isLoadingEmails" class="imap-verbose-status">
                <span class="spinner imap-verbose-spinner"></span>
                Connecting…
              </span>
            </div>
            <pre class="imap-verbose-log">{{ formattedVerboseLog }}</pre>
          </div>
        </div>

        <div v-if="processedRows.length" class="section-header">
          2) Review Rows and Class Matches
        </div>

        <div v-if="processedRows.length" class="parent-form">
          <div class="form-grid">
            <label>
              Date
              <input type="date" v-model="parent.Date" />
            </label>
            <label>
              Type
              <input type="text" v-model="parent.Type" />
            </label>
            <label>
              Check #
              <input type="text" v-model="parent.Num" />
            </label>
            <label>
              Name
              <input type="text" v-model="parent.Name" />
            </label>
            <label>
              Parent Account
              <input type="text" v-model="parent.Account" />
            </label>
            <label>
              Child Account
              <div class="account-field-wrap">
                <input
                  type="text"
                  v-model="childAccountQuery"
                  @input="onChildAccountInput"
                  @blur="hideChildAccountMatches"
                  @keydown.tab.prevent="handleChildAccountTab"
                  @focus="$event.target.select()"
                />
                <div v-if="childAccountMatches.length" class="account-dropdown">
                  <div
                    v-for="match in childAccountMatches"
                    :key="match"
                    class="dropdown-item"
                    @mousedown.prevent="selectChildAccount(match)"
                  >
                    {{ match }}
                  </div>
                </div>
              </div>
            </label>
          </div>
          <label class="full-row">
            Parent Memo
            <input type="text" v-model="parent.Memo" />
          </label>
        </div>

        <div v-if="processedRows.length" class="rows-table-wrap">
          <table class="rows-table">
            <colgroup>
              <col class="col-invoice" />
              <col class="col-date" />
              <col class="col-description" />
              <col class="col-amount" />
              <col class="col-class" />
            </colgroup>
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Email Date</th>
                <th>Property</th>
                <th class="amount-col">Amount</th>
                <th>Class</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in processedRows"
                :key="row.uid + '-' + row.invoiceNumber + '-' + index"
              >
                <td>{{ row.invoiceNumber }}</td>
                <td>{{ formatDate(row.emailDate) }}</td>
                <td>{{ row.property }}</td>
                <td class="amount-col">{{ formatCurrency(row.amount) }}</td>
                <td class="class-cell">
                  <input
                    type="text"
                    v-model="row.classQuery"
                    @input="onClassInput(index)"
                    @focus="$event.target.select()"
                    @blur="hideClassMatches(index)"
                    placeholder="Search class"
                    class="class-input"
                  />
                  <div v-if="row.classMatches.length" class="dropdown">
                    <div
                      v-for="match in row.classMatches"
                      :key="match"
                      class="dropdown-item"
                      @mousedown.prevent="selectClass(index, match)"
                    >
                      {{ match }}
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="errorMessage" class="error">{{ errorMessage }}</div>
      </div>

      <div class="bryson-import-footer">
        <button class="secondary" @click="handleClose">Cancel</button>
        <button
          class="primary"
          :disabled="isSubmitDisabled"
          @click="importSelected"
        >
          {{ isSaving ? 'Importing...' : 'Import Selected' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig';

export default {
  name: 'BrysonLawncareImport',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoadingEmails: false,
      isLoadingClasses: false,
      isProcessing: false,
      isSaving: false,
      accounts: [],
      emails: [],
      selectedEmailUids: [],
      showEmailTable: true,
      processedRows: [],
      classes: [],
      childAccountQuery: '52230 : Grounds Maintenance',
      childAccountMatches: [],
      errorMessage: '',
      showVerbosePanel: false,
      verboseLog: [],
      parent: {
        Date: new Date().toISOString().split('T')[0],
        Type: 'check',
        Num: '',
        Name: 'Bryson Lawncare LLC',
        Memo: '',
        Account: '10000 : General Checking',
        Split: '-SPLIT-',
        Class: 'Big Property Management',
        ChildAccount: '52230 : Grounds Maintenance',
        ChildSplit: '10000 : General Checking',
      },
    };
  },
  computed: {
    isBusy() {
      // Keep the verbose log visible while an IMAP fetch is in flight.
      if (this.showVerbosePanel && this.isLoadingEmails) {
        return this.isProcessing || this.isSaving;
      }
      return this.isLoadingEmails || this.isProcessing || this.isSaving;
    },

    busyMessage() {
      if (this.isSaving) return 'Importing selected emails...';
      if (this.isProcessing) return 'Processing selected emails...';
      if (this.isLoadingEmails) return 'Loading unread Bryson emails...';
      return 'Working...';
    },

    isSubmitDisabled() {
      if (this.isSaving || !this.processedRows.length) return true;
      return this.processedRows.some((row) => !row.selectedClass);
    },

    formattedVerboseLog() {
      if (!this.verboseLog.length) {
        return this.isLoadingEmails
          ? 'Connecting…'
          : 'No IMAP log yet. Click Verbose or Refresh.';
      }
      return this.verboseLog
        .map((row) => {
          const ms = String(row.ms ?? 0).padStart(5, ' ');
          const step = String(row.step || 'log').padEnd(8, ' ');
          return `${ms}ms  [${step}] ${row.detail || ''}`;
        })
        .join('\n');
    },
  },
  watch: {
    show: {
      immediate: true,
      async handler(newValue) {
        if (newValue) {
          await this.$nextTick();
          const modal = this.$refs.brysonImportModal;
          this.applySavedPosition(modal);
          this.makeDraggable(modal);
          await this.initialize();
        }
      },
    },
  },
  methods: {
    async initialize() {
      this.errorMessage = '';
      this.showVerbosePanel = false;
      this.verboseLog = [];
      this.selectedEmailUids = [];
      this.showEmailTable = true;
      this.processedRows = [];
      this.parent.Date = new Date().toISOString().split('T')[0];
      this.parent.Memo = '';
      this.parent.ChildAccount = '52230 : Grounds Maintenance';
      this.childAccountQuery = this.parent.ChildAccount;
      this.childAccountMatches = [];
      await Promise.all([
        this.loadEmails(),
        this.loadClasses(),
        this.loadAccounts(),
      ]);
    },

    async loadEmails() {
      const verbose = this.showVerbosePanel;
      try {
        this.isLoadingEmails = true;
        this.errorMessage = '';
        if (verbose) {
          this.verboseLog = [
            {
              ms: 0,
              step: 'client',
              detail: 'GET /api/bryson-lawncare-emails/list?verbose=1',
            },
          ];
        }
        const response = await axios.get('/api/bryson-lawncare-emails/list', {
          params: verbose ? { verbose: 1 } : {},
        });
        this.emails = response.data?.emails || [];
        if (verbose) {
          this.verboseLog = response.data?.verboseLog || [];
        }

        const selectedSet = new Set(this.selectedEmailUids);
        this.selectedEmailUids = this.emails
          .filter((row) => selectedSet.has(row.uid))
          .map((row) => row.uid);
      } catch (error) {
        console.error('Error loading Bryson emails:', error);
        this.errorMessage =
          error.response?.data?.error ||
          'Could not load Bryson payment request emails.';
        if (verbose) {
          const serverLog = error.response?.data?.verboseLog;
          this.verboseLog = Array.isArray(serverLog)
            ? [...serverLog]
            : [
                {
                  ms: 0,
                  step: 'client',
                  detail: `${error.message}${
                    error.code ? ` (${error.code})` : ''
                  }`,
                },
              ];
          if (error.response?.data?.errorDetail) {
            this.verboseLog.push({
              ms: 0,
              step: 'error',
              detail: error.response.data.errorDetail,
            });
          }
        }
      } finally {
        this.isLoadingEmails = false;
      }
    },

    toggleVerbosePanel() {
      this.showVerbosePanel = !this.showVerbosePanel;
      if (this.showVerbosePanel) {
        this.loadEmails();
      }
    },

    async loadClasses() {
      try {
        this.isLoadingClasses = true;
        const response = await axios.get('/api/get-classes');
        this.classes = response.data || [];
      } catch (error) {
        console.error('Error loading classes:', error);
      } finally {
        this.isLoadingClasses = false;
      }
    },

    async loadAccounts() {
      try {
        const response = await axios.get('/api/get-chart-of-accounts');
        this.accounts = response.data || [];
      } catch (error) {
        console.error('Error loading accounts:', error);
      }
    },

    isEmailSelected(uid) {
      return this.selectedEmailUids.includes(uid);
    },

    toggleEmail(uid) {
      if (this.isProcessing || this.isSaving) return;
      if (this.isEmailSelected(uid)) {
        this.selectedEmailUids = this.selectedEmailUids.filter(
          (id) => id !== uid,
        );
      } else {
        this.selectedEmailUids = [...this.selectedEmailUids, uid];
      }
    },

    selectAllEmails() {
      if (this.isProcessing || this.isSaving) return;
      this.selectedEmailUids = this.emails.map((email) => email.uid);
    },

    async processSelection() {
      if (!this.selectedEmailUids.length) return;

      try {
        this.isProcessing = true;
        this.errorMessage = '';
        this.processedRows = [];

        const response = await axios.post(
          '/api/bryson-lawncare-emails/process',
          {
            uids: this.selectedEmailUids,
          },
        );

        const rows = response.data?.rows || [];
        this.processedRows = rows.map((row) => {
          const initialClass = row.suggestedClass || '';
          return {
            ...row,
            selectedClass: initialClass,
            classQuery: initialClass,
            classMatches: [],
          };
        });

        if (this.processedRows.length) {
          this.parent.Date =
            this.processedRows[0].emailDate || this.parent.Date;
          this.parent.Memo =
            this.parent.Memo ||
            `Bryson Lawncare invoices imported on ${new Date().toLocaleDateString('en-US')}`;
          this.showEmailTable = false;
        }
      } catch (error) {
        console.error('Error processing Bryson emails:', error);
        this.errorMessage =
          error?.response?.data?.error ||
          'Could not process selected emails. Please try again.';
      } finally {
        this.isProcessing = false;
      }
    },

    onClassInput(index) {
      const row = this.processedRows[index];
      if (!row) return;

      const query = String(row.classQuery || '')
        .toLowerCase()
        .trim();
      row.selectedClass = '';

      if (!query) {
        row.classMatches = [];
        return;
      }

      const tokens = query
        .split(',')
        .map((token) => token.trim())
        .filter(Boolean);

      let matches = this.classes;
      tokens.forEach((token) => {
        matches = matches.filter((item) => item.toLowerCase().includes(token));
      });

      row.classMatches = matches.slice(0, 20);
    },

    hideClassMatches(index) {
      setTimeout(() => {
        if (this.processedRows[index]) {
          this.processedRows[index].classMatches = [];
        }
      }, 120);
    },

    selectClass(index, classValue) {
      const row = this.processedRows[index];
      if (!row) return;
      row.selectedClass = classValue;
      row.classQuery = classValue;
      row.classMatches = [];
    },

    normalizeAccountOption(account) {
      if (typeof account === 'string') return account;
      if (!account || !account.accountNumber || !account.accountName) return '';
      return `${account.accountNumber} : ${account.accountName}`;
    },

    onChildAccountInput() {
      const query = String(this.childAccountQuery || '')
        .toLowerCase()
        .trim();

      if (!query) {
        this.parent.ChildAccount = this.childAccountQuery || '';
        this.childAccountMatches = [];
        return;
      }

      this.childAccountMatches = this.accounts
        .map((item) => this.normalizeAccountOption(item))
        .filter((value) => value && value.toLowerCase().includes(query))
        .slice(0, 20);

      if (this.childAccountMatches.length > 0) {
        this.parent.ChildAccount = this.childAccountMatches[0];
      } else {
        this.parent.ChildAccount = this.childAccountQuery;
      }
    },

    hideChildAccountMatches() {
      setTimeout(() => {
        this.childAccountMatches = [];
      }, 120);
    },

    selectChildAccount(match) {
      this.parent.ChildAccount = match;
      this.childAccountQuery = match;
      this.childAccountMatches = [];
    },

    focusNextInput(currentElement) {
      if (!currentElement || !this.$el) return;

      const inputs = Array.from(
        this.$el.querySelectorAll(
          'input, button, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.disabled && el.offsetParent !== null);

      const currentIndex = inputs.indexOf(currentElement);
      if (currentIndex >= 0 && currentIndex < inputs.length - 1) {
        inputs[currentIndex + 1].focus();
      }
    },

    handleChildAccountTab(event) {
      if (this.childAccountMatches.length > 0) {
        this.selectChildAccount(this.childAccountMatches[0]);
      } else {
        this.parent.ChildAccount = this.childAccountQuery || '';
      }

      this.focusNextInput(event.target);
    },

    applySavedPosition(el) {
      if (!el || !(el instanceof HTMLElement)) return;

      const savedTop = localStorage.getItem('brysonImportTop');
      const savedLeft = localStorage.getItem('brysonImportLeft');

      if (savedTop && savedLeft) {
        el.style.top = savedTop;
        el.style.left = savedLeft;
        return;
      }

      const top = Math.max((window.innerHeight - el.offsetHeight) / 2, 20);
      const left = Math.max((window.innerWidth - el.offsetWidth) / 2, 20);
      el.style.top = `${top}px`;
      el.style.left = `${left}px`;
    },

    makeDraggable(el) {
      if (!el || !(el instanceof HTMLElement)) return;

      let pos1 = 0;
      let pos2 = 0;
      let pos3 = 0;
      let pos4 = 0;
      const dragElement = el.querySelector('.draggable-header');

      if (dragElement) {
        dragElement.onmousedown = dragMouseDown;
      } else {
        el.onmousedown = dragMouseDown;
      }

      function dragMouseDown(e) {
        e.preventDefault();
        pos3 = e.clientX;
        pos4 = e.clientY;
        document.onmouseup = closeDragElement;
        document.onmousemove = elementDrag;
      }

      function elementDrag(e) {
        e.preventDefault();
        pos1 = pos3 - e.clientX;
        pos2 = pos4 - e.clientY;
        pos3 = e.clientX;
        pos4 = e.clientY;
        el.style.top = `${el.offsetTop - pos2}px`;
        el.style.left = `${el.offsetLeft - pos1}px`;
      }

      function closeDragElement() {
        document.onmouseup = null;
        document.onmousemove = null;
        localStorage.setItem('brysonImportTop', el.style.top);
        localStorage.setItem('brysonImportLeft', el.style.left);
      }
    },

    formatDate(dateValue) {
      if (!dateValue) return '';
      const date = new Date(dateValue);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-US');
    },

    formatCurrency(value) {
      return Number(value || 0).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      });
    },

    async importSelected() {
      if (this.isSubmitDisabled) return;

      try {
        this.isSaving = true;
        this.errorMessage = '';

        const payload = {
          parent: this.parent,
          selectedUids: this.selectedEmailUids,
          children: this.processedRows.map((row) => ({
            uid: row.uid,
            invoiceNumber: row.invoiceNumber,
            property: row.property,
            amount: row.amount,
            emailDate: row.emailDate,
            class: row.selectedClass,
          })),
        };

        // Import marks IMAP messages read after DB commit; can exceed the
        // default 20s axios timeout when mail is slow.
        await axios.post('/api/bryson-lawncare-emails/import', payload, {
          timeout: 120000,
        });
        this.handleClose();
      } catch (error) {
        console.error('Error importing Bryson rows:', error);
        this.errorMessage =
          error?.response?.data?.error ||
          'Import failed. Please review and try again.';
      } finally {
        this.isSaving = false;
      }
    },

    handleClose() {
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
.bryson-import-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 99998;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bryson-import-modal {
  width: 1040px;
  max-width: 98vw;
  height: 86vh;
  min-height: 680px;
  max-height: 86vh;
  background: white;
  border-radius: 8px;
  border: 1px solid #444;
  display: flex;
  flex-direction: column;
  position: absolute;
}

.busy-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.65);
  z-index: 30;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.spinner {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 3px solid #d8c5e2;
  border-top-color: #4b0082;
  animation: spin 0.8s linear infinite;
}

.busy-label {
  font-size: 10pt;
  color: #4b0082;
  font-weight: 600;
}

.bryson-import-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: #333;
  color: white;
}

.draggable-header {
  cursor: move;
  user-select: none;
}

.bryson-import-header h3 {
  margin: 0;
  font-size: 13pt;
}

.close-btn {
  border: 1px solid #888;
  background: #4b0082;
  color: white;
  border-radius: 4px;
  padding: 4px 10px;
  cursor: pointer;
}

.bryson-import-body {
  padding: 10px;
  flex: 1;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.section-header {
  font-weight: 600;
  margin: 4px 0;
}

.section-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.toggle-email-table-btn {
  font-size: 9pt;
}

.email-table-wrap {
  border: 1px solid #ddd;
  border-radius: 6px;
  max-height: 540px;
  overflow: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.email-table-wrap::-webkit-scrollbar {
  width: 0;
  height: 0;
}

.email-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 9pt;
}

.email-table th,
.email-table td {
  border-bottom: 1px solid #ececec;
  padding: 6px;
  text-align: left;
}

.email-table .check-col {
  width: 32px;
  text-align: center;
}

.email-table .date-col {
  width: 110px;
}

.email-table .invoice-col {
  width: 100px;
}

.email-table tbody tr {
  cursor: pointer;
}

.email-table tbody tr:nth-child(even) {
  background-color: #efefef;
}

.email-table tbody tr.selected-row {
  background-color: rgb(226 203 235) !important;
}

.email-actions {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

.verbose-active {
  background: #4b0082;
  color: white;
  border-color: #4b0082;
}

.imap-verbose-panel {
  margin-top: 8px;
  border: 1px solid #cbb7d4;
  border-radius: 6px;
  background: #1e1a22;
  color: #e8dcee;
  max-width: 720px;
}

.imap-verbose-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-size: 9pt;
  font-weight: 600;
  padding: 6px 8px;
  background: #3a2b42;
  border-bottom: 1px solid #cbb7d4;
  border-radius: 6px 6px 0 0;
}

.imap-verbose-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  color: #f3e9f8;
}

.imap-verbose-spinner {
  width: 14px;
  height: 14px;
  border-width: 2px;
  border-color: #cbb7d4;
  border-top-color: #fff;
  flex-shrink: 0;
}

.imap-verbose-log {
  margin: 0;
  padding: 8px;
  max-height: 180px;
  overflow: auto;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 8.5pt;
  line-height: 1.35;
  white-space: pre-wrap;
  word-break: break-word;
}

.parent-form {
  border: 1px solid #ddd;
  padding: 8px;
  border-radius: 6px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

label {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 9pt;
}

input {
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 6px;
}

.account-field-wrap {
  position: relative;
  width: 100%;
}

.account-field-wrap input {
  width: 100%;
  box-sizing: border-box;
}

.account-dropdown {
  position: absolute;
  left: 0;
  right: 0;
  top: 34px;
  background: #181818b5;
  border-top: 1px solid #bbb;
  border-left: 1px solid #bbb;
  max-height: 220px;
  overflow-y: auto;
  z-index: 60;
  color: white;
}

.full-row {
  margin-top: 8px;
}

.rows-table-wrap {
  border: 1px solid #ddd;
  border-radius: 6px;
  max-height: 42vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 220px;
  box-sizing: border-box;
}

.rows-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 9pt;
  table-layout: fixed;
}

.rows-table .col-invoice {
  width: 72px;
}

.rows-table .col-date {
  width: 110px;
}

.rows-table .col-description {
  width: 220px;
}

.rows-table .col-amount {
  width: 110px;
}

.rows-table .col-class {
  width: auto;
}

.rows-table tbody tr:nth-child(even) {
  background-color: #efefef;
}

.rows-table th,
.rows-table td {
  border-bottom: 1px solid #ececec;
  padding: 3px 6px;
  vertical-align: top;
  word-break: break-word;
}

.rows-table th {
  text-align: left;
}

.rows-table th.amount-col,
.rows-table td.amount-col {
  text-align: right;
}

.class-cell {
  min-width: 180px;
  position: relative;
}

.class-input {
  width: min(100%, 360px);
  max-width: 360px;
  padding: 3px 6px;
}

.dropdown {
  position: absolute;
  left: 6px;
  width: min(calc(100% - 12px), 360px);
  top: 34px;
  background: #181818b5;
  border-top: 1px solid #bbb;
  border-left: 1px solid #bbb;
  max-height: 220px;
  overflow-y: auto;
  z-index: 50;
  color: white;
}

.dropdown-item {
  padding: 5px;
  cursor: pointer;
}

.dropdown-item:hover {
  background-color: #f0f0f0;
  color: #333;
}

.warning {
  margin: 8px 0;
  background: #fff1cc;
  border: 1px solid #e6c86a;
  color: #7a5a00;
  padding: 8px;
  border-radius: 4px;
}

.error {
  margin-top: 8px;
  color: #b00020;
  font-size: 9pt;
}

.hint {
  color: #777;
  font-size: 9pt;
}

.bryson-import-footer {
  border-top: 1px solid #ddd;
  padding: 10px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.secondary,
.primary {
  border: 1px solid #999;
  border-radius: 4px;
  padding: 6px 12px;
  cursor: pointer;
}

.primary {
  background: #4b0082;
  color: white;
  border-color: #4b0082;
}

.primary:disabled,
.secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
